<template>
  <div class="view-container surveys-page">
    <div class="extended-filter-block">
      <h2>Опросы</h2>
    </div>

    <div class="card card__with_smaller_table banner-card">
      <div v-if="hasSubscription" class="surveys-banner__status">
        <CheckOutlined class="surveys-banner__check" />
        <span class="surveys-banner__active-label">Доступно по подписке</span>
        <a-divider type="vertical" style="margin: 0" />
        <span class="surveys-banner__expires">
          Истекает: <strong>{{ subscriptionExpiresAt }}</strong>
        </span>
      </div>

      <p class="surveys-banner__description">
        Автоматизированный сбор данных о состоянии сотрудников. Система замеряет уровень стресса, вовлеченность
        и прогресс в развитии ЭИ, превращая ответы в наглядные графики и отчёты.
      </p>

      <div
          class="surveys-banner__actions"
          :class="{ 'surveys-banner__actions--split': !hasSubscription }"
      >
        <a-button
            v-if="!hasSubscription"
            class="primary-button surveys-banner__primary-btn"
            @click="handleOrderForm"
        >
          Оформить подписку
        </a-button>

        <a-button
            class="secondary-button surveys-banner__details-btn"
            @click="handleShowDetails"
        >
          Подробнее об опросах
        </a-button>
      </div>
    </div>

    <div class="surveys-grid">
      <div
          v-for="survey in surveys"
          :key="survey.id"
          class="card survey-card"
          :class="{ 'survey-card--locked': !hasSubscription }"
      >
        <SettingOutlined
            v-if="hasSubscription"
            class="survey-card__action-icon"
            @click="handleSettings(survey)"
        />
        <LockFilled
            v-else
            class="survey-card__action-icon survey-card__action-icon--lock"
        />

        <a-tag
            v-if="hasSubscription"
            class="survey-card__status-tag"
            :class="{'ant-tag-green': survey.active, 'ant-tag-red': !survey.active}"
        >
          {{ survey.active ? 'Активен' : 'Неактивен' }}
        </a-tag>

        <h3 class="survey-card__title">{{ survey.title }}</h3>

        <p class="survey-card__description">{{ survey.description }}</p>

        <ul class="survey-card__meta">
          <li class="survey-card__meta-item">
            <img
                :src="SurveyUsersIcon"
                alt=""
                class="survey-card__meta-icon"
            />
            <span>{{ survey.audience }}</span>
          </li>
          <li class="survey-card__meta-item">
            <img
                :src="SurveyClockIcon"
                alt=""
                class="survey-card__meta-icon"
            />
            <span>{{ survey.duration }}</span>
          </li>
          <li class="survey-card__meta-item">
            <img
                :src="SurveyCalendarIcon"
                alt=""
                class="survey-card__meta-icon"
            />
            <span>{{ survey.schedule }}</span>
          </li>
        </ul>
      </div>
    </div>

    <NewOrderForm
        v-if="showOrderForm"
        :open="showOrderForm"
        @close="showOrderForm = false"
    />

    <SurveySettingsModal
        v-if="settingsModalOpen"
        :open="settingsModalOpen"
        :survey="activeSurvey"
        :group-list="groups"
        :groups-loading="groupsLoading"
        @close="closeSettings"
        @submitted="handleSettingsSubmitted"
    />
  </div>
</template>

<script>
import { computed, onBeforeMount, ref } from 'vue';
import { useStore } from 'vuex';
import {
  CheckOutlined,
  LockFilled,
  SettingOutlined,
} from '@ant-design/icons-vue';

import NewOrderForm from '@/views/Orders/OrderForm.vue';
import SurveySettingsModal from '@/views/Surveys/SurveySettingsModal.vue';
import { getWordFormBasedOnTheCount } from '@/views/Report/utils';
import SurveyCalendarIcon from '@/assets/images/surveys/calendar.svg';
import SurveyClockIcon from '@/assets/images/surveys/clock.svg';
import SurveyUsersIcon from '@/assets/images/surveys/users.svg';

const WEEKDAY_LABELS = {
  monday: 'понедельник',
  tuesday: 'вторник',
  wednesday: 'среда',
  thursday: 'четверг',
  friday: 'пятница',
  saturday: 'суббота',
  sunday: 'воскресенье',
};

const SURVEY_META_BY_PERIOD = {
  weekly: {
    description:
        'Замеряет текущее состояние и помогает предотвратить «пожары» в конце недели.',
    duration: '2 вопроса, 5 минут',
    frequencyText: 'Опрос проводится еженедельно.',
    schedulePrefix: 'Еженедельно',
  },
  monthly: {
    description:
        'Ежемесячный чекап прогресса. Проверяем на практике, как команда применяет эмоциональный интеллект в рабочих делах.',
    duration: '5 вопросов, 10 минут',
    frequencyText: 'Опрос проводится ежемесячно в последнюю неделю месяца.',
    schedulePrefix: 'Ежемесячно',
  },
  quarterly: {
    description:
        'Квартальный аудит ресурсного состояния команды. Помогает вовремя обнаружить скрытое выгорание, падение интереса к задачам и накопленный стресс, чтобы вовремя скорректировать нагрузку и сохранить ключевых сотрудников.',
    duration: '40 вопросов, 30 минут',
    frequencyText: 'Опрос проводится ежеквартально.',
    schedulePrefix: 'Ежеквартально',
  },
};

export default {
  name: 'surveys-page',
  components: {
    NewOrderForm,
    SurveySettingsModal,
    CheckOutlined,
    LockFilled,
    SettingOutlined,
  },
  setup() {
    const store = useStore();

    const showOrderForm = ref(false);

    const permittedSubscriptions = computed(
        () => store.state.subscription?.permittedSubscriptions ?? []
    );

    const hasSubscription = computed(
        () => Array.isArray(permittedSubscriptions.value) && permittedSubscriptions.value.length > 0
    );

    const subscriptionExpiresAt = computed(
        () => permittedSubscriptions.value?.[0]?.expired_at || '—'
    );

    const surveys = ref([]);
    const groupsLoading = ref(false);

    const allGroups = computed(() => store.state.group?.groups ?? []);

    const groups = computed(() => allGroups.value.map((group) => ({
      id: group.id,
      name: group.name,
    })));

    const getGroupId = (group) => (group && typeof group === 'object' ? group.id : group);

    const getGroupName = (surveyGroup) => {
      if (surveyGroup && surveyGroup?.name) {
        return surveyGroup.name;
      }

      const groupId = getGroupId(surveyGroup);

      return groups.value.find((group) => String(group.id) === String(groupId))?.name;
    };

    const getGroupsLabel = (surveyGroups) => {
      if (surveyGroups === 'all' || !Array.isArray(surveyGroups) || !surveyGroups.length) {
        return 'Все сотрудники';
      }

      const groupNames = surveyGroups
          .map((surveyGroup) => getGroupName(surveyGroup))
          .filter(Boolean);

      if (groupNames.length) {
        return groupNames.join(', ');
      }

      return `${surveyGroups.length} ${getWordFormBasedOnTheCount(surveyGroups.length, 'группа', 'группы', 'групп')}`;
    };

    const mapSettingToSurvey = (setting) => {
      const period = setting.survey?.period ?? 'weekly';
      const meta = SURVEY_META_BY_PERIOD[period] ?? SURVEY_META_BY_PERIOD.weekly;
      const weekday = WEEKDAY_LABELS[setting.day_of_week] ?? setting.day_of_week;
      const time = setting.time ?? '12:00';

      return {
        id: setting.survey?.id,
        title: setting.survey?.name ?? '',
        description: meta.description,
        audience: getGroupsLabel(setting.groups),
        duration: meta.duration,
        schedule: `${meta.schedulePrefix}, ${weekday}, ${time}`,
        frequencyText: meta.frequencyText,
        active: setting.active ?? true,
        settings: setting,
      };
    };

    const loadSurveySettings = () => {
      return store.dispatch('periodicSurveys/getSettings').then((settings) => {
        surveys.value = settings.map((setting) => mapSettingToSurvey(setting));
      });
    };

    const loadGroups = () => {
      if (allGroups.value.length) {
        return Promise.resolve();
      }

      groupsLoading.value = true;

      return store.dispatch('group/getGroups').finally(() => {
        groupsLoading.value = false;
      });
    };

    const settingsModalOpen = ref(false);
    const activeSurvey = ref(null);

    const handleOrderForm = () => {
      showOrderForm.value = true;
    };

    const handleShowDetails = () => {
      window.open('https://way2wei.ru', '_blank');
    };

    const handleSettings = (survey) => {
      activeSurvey.value = survey;
      settingsModalOpen.value = true;
    };

    const closeSettings = () => {
      settingsModalOpen.value = false;
      activeSurvey.value = null;
    };

    const handleSettingsSubmitted = () => {
      loadSurveySettings();
    };

    onBeforeMount(() => {
      loadGroups().catch(() => null).finally(() => {
        loadSurveySettings();
      });
    });

    return {
      SurveyCalendarIcon,
      SurveyClockIcon,
      SurveyUsersIcon,
      surveys,
      groups,
      groupsLoading,
      hasSubscription,
      subscriptionExpiresAt,
      showOrderForm,
      settingsModalOpen,
      activeSurvey,
      handleOrderForm,
      handleShowDetails,
      handleSettings,
      closeSettings,
      handleSettingsSubmitted,
    };
  },
};
</script>

<style scoped lang="scss">
.view-container {
  padding-top: 0;
  margin-top: 0;
}

.surveys-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 25px;
    align-items: center;
    justify-content: space-between;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
}

.banner-card {
  box-shadow: none;
}

.surveys-banner {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px;
  background-color: #ffffff;
  border-radius: 8px;
}

.surveys-banner__status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  margin-bottom: 16px;
}

.surveys-banner__check {
  color: var(--main-green);
  font-size: 14px;
}

.surveys-banner__active-label {
  color: var(--main-green);
}

.surveys-banner__separator {
  color: var(--neutral-fade-black-font);
}

.surveys-banner__expires {
  strong {
    font-weight: 600;
  }
}

.surveys-banner__description {
  margin: 0 0 16px;
  max-width: 800px;
  text-align: left;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.surveys-banner__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.surveys-banner__actions--split {
  .surveys-banner__details-btn {
    margin-left: auto;
  }
}

.surveys-banner__primary-btn {
  min-width: 180px;
}

.surveys-banner__details-btn {
  min-width: 180px;
}

.surveys-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: $tablet-breakpoint) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.survey-card {
  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: none;
}

.survey-card__status-tag {
  align-self: flex-start;
  margin: 0;
  padding: 4px 8px;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
}

.survey-card__action-icon {
  position: absolute;
  top: 20px;
  right: 20px;
  color: var(--neutral-fade-black-font);
  font-size: 16px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.survey-card__action-icon:not(.survey-card__action-icon--lock):hover {
  color: var(--main-green);
}

.survey-card__action-icon--lock {
  cursor: default;
}

.survey-card--locked {
  .survey-card__title {
    color: rgba(0, 0, 0, 0.65);
  }
}

.survey-card__title {
  margin: 0;
  padding-right: 28px;
  text-align: left;
  font-style: normal;
  color: var(--main-font);
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
}

.survey-card__description {
  margin: 0;
  text-align: left;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.survey-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
}

.survey-card__meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.65);
}

.survey-card__meta-icon {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  display: block;
}

@media (max-width: $mobile-breakpoint) {
  .surveys-banner {
    padding: 16px;
  }

  .surveys-banner__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .surveys-banner__actions--split {
    .surveys-banner__details-btn {
      margin-left: 0;
    }
  }

  .surveys-banner__primary-btn,
  .surveys-banner__details-btn {
    width: 100%;
  }

  .survey-card {
    padding: 16px;
  }
}
</style>
